/**
 * 微信分享工具类
 * 用于配置和管理微信分享功能
 */

/**
 * 初始化微信分享配置
 * @param {Object} config - 微信配置对象
 * @param {string} config.appId - 微信公众号AppID
 * @param {string} config.timestamp - 时间戳
 * @param {string} config.nonceStr - 随机字符串
 * @param {string} config.signature - 签名
 */
export function initWechatShare(config) {
  return new Promise((resolve, reject) => {
    // 检查是否在微信环境中
    if (typeof wx === 'undefined') {
      console.warn('微信JS-SDK未加载，请先引入微信JS-SDK')
      reject(new Error('微信JS-SDK未加载'))
      return
    }

    // 配置微信JS-SDK
    wx.config({
      debug: false, // 生产环境设为false
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: [
        'updateAppMessageShareData', // 分享给朋友
        'updateTimelineShareData',  // 分享到朋友圈
        'onMenuShareAppMessage',    // 旧版分享给朋友（兼容）
        'onMenuShareTimeline',      // 旧版分享到朋友圈（兼容）
        'openLocation'              // 使用微信内置地图查看位置
      ]
    })

    // 配置成功回调
    wx.ready(() => {
      console.log('微信JS-SDK配置成功')
      resolve()
    })

    // 配置失败回调
    wx.error((res) => {
      console.error('微信JS-SDK配置失败:', res)
      reject(res)
    })
  })
}

/**
 * 设置分享给朋友的内容
 * @param {Object} shareData - 分享数据
 * @param {string} shareData.title - 分享标题
 * @param {string} shareData.desc - 分享描述
 * @param {string} shareData.link - 分享链接
 * @param {string} shareData.imgUrl - 分享图片URL
 */
export function setShareToFriend(shareData) {
  if (typeof wx === 'undefined') {
    console.warn('微信JS-SDK未加载')
    return
  }

  // 新版API
  wx.updateAppMessageShareData({
    title: shareData.title || '广州农业农村消费地图',
    desc: shareData.desc || '探索广州农业农村精彩内容',
    link: shareData.link || window.location.href,
    imgUrl: shareData.imgUrl || '',
    success: () => {
      console.log('设置分享给朋友成功')
    },
    cancel: () => {
      console.log('取消分享给朋友')
    }
  })

  // 兼容旧版API
  wx.onMenuShareAppMessage({
    title: shareData.title || '广州农业农村消费地图',
    desc: shareData.desc || '探索广州农业农村精彩内容',
    link: shareData.link || window.location.href,
    imgUrl: shareData.imgUrl || '',
    type: 'link',
    success: () => {
      console.log('设置分享给朋友成功（旧版API）')
    },
    cancel: () => {
      console.log('取消分享给朋友（旧版API）')
    }
  })
}

/**
 * 设置分享到朋友圈的内容
 * @param {Object} shareData - 分享数据
 * @param {string} shareData.title - 分享标题
 * @param {string} shareData.link - 分享链接
 * @param {string} shareData.imgUrl - 分享图片URL
 */
export function setShareToTimeline(shareData) {
  if (typeof wx === 'undefined') {
    console.warn('微信JS-SDK未加载')
    return
  }

  // 新版API
  wx.updateTimelineShareData({
    title: shareData.title || '广州农业农村消费地图',
    link: shareData.link || window.location.href,
    imgUrl: shareData.imgUrl || '',
    success: () => {
      console.log('设置分享到朋友圈成功')
    },
    cancel: () => {
      console.log('取消分享到朋友圈')
    }
  })

  // 兼容旧版API
  wx.onMenuShareTimeline({
    title: shareData.title || '广州农业农村消费地图',
    link: shareData.link || window.location.href,
    imgUrl: shareData.imgUrl || '',
    success: () => {
      console.log('设置分享到朋友圈成功（旧版API）')
    },
    cancel: () => {
      console.log('取消分享到朋友圈（旧版API）')
    }
  })
}

/**
 * 同时设置分享给朋友和分享到朋友圈
 * @param {Object} shareData - 分享数据
 * @param {string} shareData.title - 分享标题
 * @param {string} shareData.desc - 分享描述（仅用于分享给朋友）
 * @param {string} shareData.link - 分享链接
 * @param {string} shareData.imgUrl - 分享图片URL
 */
export function setWechatShare(shareData) {
  setShareToFriend(shareData)
  setShareToTimeline(shareData)
}

/**
 * 检查是否在微信环境中
 * @returns {boolean} 是否在微信中
 */
export function isWechat() {
  const ua = navigator.userAgent.toLowerCase()
  return /micromessenger/i.test(ua)
}

/**
 * 获取当前页面URL（用于分享链接）
 * @returns {string} 当前页面URL
 */
export function getCurrentUrl() {
  return window.location.href.split('#')[0] // 去除hash部分
}

/**
 * 默认分享配置
 */
export const defaultShareConfig = {
  title: '广州农业农村消费地图',
  desc: '探索广州农业农村精彩内容，发现特色农产品、都市农业、农业文化遗产等',
  link: getCurrentUrl(),
  imgUrl: '' // 需要设置完整的图片URL
}

